import { createContext, useContext, useState, useEffect } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { User as AppUser } from "@/models/user";
import { AuthContextType, AuthProviderProps } from "./types";
import * as authActions from "./auth-actions";

// Create the auth context with default values
const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
    loading: false,
  error: null,
  signIn: async () => ({ success: false }),
  signUp: async () => ({ success: false }),
  signOut: async () => {},
  logout: async () => {},
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  setSession: () => {},
  sendPasswordResetEmail: async () => ({ success: false }),
  updatePassword: async () => ({ success: false }),
  signInWithProvider: async () => ({ success: false }),
});

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log("Auth state changed:", event, currentSession?.user?.email);
        
        if (event === "PASSWORD_RECOVERY") {
          console.log("Password recovery event detected");
          // We don't do anything here as the UI for password update is handled in ResetPassword.tsx
        }
        
        setSession(currentSession);
        
        if (currentSession?.user) {
          // Use setTimeout to prevent potential deadlock with auth state changes
          setTimeout(async () => {
            try {
              const userData = await authActions.fetchUserProfile(currentSession.user.id);
              setUser(userData);
            } catch (error) {
              console.error("Error in auth change:", error);
              setUser(null);
            }
          }, 0);
        } else {
          setUser(null);
        }
      }
    );

    // Get initial session
    const getSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setSession(data.session);
        
        if (data.session?.user) {
          const userData = await authActions.fetchUserProfile(data.session.user.id);
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Error getting session:", error);
        setError(error instanceof Error ? error : new Error(String(error)));
      } finally {
        setLoading(false);
      }
    };
    
    getSession();

    // Refresh session periodically 
    const refreshSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error refreshing session:', error);
          return;
        }
        if (data?.session) {
          setSession(data.session);
        }
      } catch (err) {
        console.error('Unexpected error refreshing session:', err);
      }
    };

    refreshSession();
    const interval = setInterval(refreshSession, 1000 * 60 * 50);

    // Cleanup function
    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  // Implementation of auth functions
  const signInHandler = async (email: string, password: string) => {
    const result = await authActions.signIn(email, password);
    if (result.success && result.session) {
      setSession(result.session);
    }
    return { success: result.success, error: result.error };
  };

  const signUpHandler = async (email: string, password: string, name: string, profileType: string) => {
    const result = await authActions.signUp(email, password, name, profileType);
    if (result.success && result.session) {
      setSession(result.session);
    }
    return { success: result.success, error: result.error };
  };

  const signOutHandler = async () => {
    const result = await authActions.signOut();
    if (result.success) {
      setSession(null);
      setUser(null);
    }
  };

  const value = {
    session,
    user,
    loading,
    error,
    signIn: signInHandler,
    login: signInHandler, // Alias
    signUp: signUpHandler,
    register: signUpHandler, // Alias
    signOut: signOutHandler,
    logout: signOutHandler, // Alias
    setSession,
    sendPasswordResetEmail: authActions.sendPasswordResetEmail,
    updatePassword: authActions.updatePassword,
    signInWithProvider: authActions.signInWithProvider,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
